import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
    Home, 
    FileText, 
    Settings, 
    Package, 
    LogOut, 
    Menu,
    X,
    User,
    ChevronRight,
    ChevronDown,
    Users,
    Mail,
    FileEdit,
    PlusCircle,
    Cog,
    ScrollText,
    Paintbrush,
    BarChart3,
    UserPlus
} from 'lucide-react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Desktop Sidebar Navigation
const Sidebar = ({ appName }) => {
    const { user, logout, isAdmin, isGlobalAdmin, token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Auto-expand settings sub-menu when on a settings sub-page
    const settingsSubPaths = ['/admin/settings', '/admin/users', '/admin/email-templates', '/admin/quote-settings', '/admin/app-settings', '/admin/logs', '/admin/report-designer'];
    const isOnSettingsPage = settingsSubPaths.some(p => location.pathname.startsWith(p));

    useEffect(() => {
        if (isOnSettingsPage) setSettingsOpen(true);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const topItems = [
        { to: '/admin', label: 'Dashboard', icon: Home },
        { to: '/admin/quotes', label: 'Quotes', icon: FileText },
        { to: '/admin/spares', label: 'Spares', icon: Package },
        { to: '/admin/customers', label: 'Customers', icon: Users },
        { to: '/admin/engineer-history', label: 'Engineers', icon: BarChart3 },
    ];

    const settingsSubItems = [
        { to: '/admin/settings', label: 'General', icon: Settings },
        { to: '/admin/users', label: 'Users', icon: UserPlus },
        { to: '/admin/email-templates', label: 'Emails', icon: Mail },
        { to: '/admin/quote-settings', label: 'PDF Settings', icon: FileEdit },
        ...(isGlobalAdmin() ? [
            { to: '/admin/app-settings', label: 'App Settings', icon: Cog },
            { to: '/admin/logs', label: 'Logs', icon: ScrollText },
            { to: '/admin/report-designer', label: 'Report Designer', icon: Paintbrush },
        ] : []),
    ];

    const engineerItems = [
        { to: '/', label: 'New Quote', icon: PlusCircle },
        { to: '/quotes', label: 'My Quotes', icon: FileText },
    ];

    const isActive = (to) => {
        if (to === '/admin' && location.pathname === '/admin') return true;
        if (to === '/' && location.pathname === '/') return true;
        if (to !== '/' && to !== '/admin' && location.pathname.startsWith(to)) return true;
        return false;
    };

    const renderNavLink = (item) => {
        const Icon = item.icon;
        const active = isActive(item.to);
        return (
            <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all rounded-lg ${
                    active 
                        ? 'bg-brand-lime/10 text-brand-charcoal border-l-4 border-brand-lime -ml-px' 
                        : 'text-brand-gray-600 hover:bg-brand-gray-50 hover:text-brand-charcoal'
                }`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
                <Icon className={`h-5 w-5 ${active ? 'text-brand-lime' : ''}`} />
                {item.label}
            </Link>
        );
    };

    return (
        <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border h-screen sticky top-0" data-testid="desktop-sidebar">
            {/* App Name */}
            <div className="p-6 border-b border-border min-h-[73px] flex items-center">
                {appName ? (
                    <Link to={isAdmin() ? '/admin' : '/'} className="text-xl font-bold font-heading text-brand-charcoal">
                        {appName}
                    </Link>
                ) : (
                    <div className="h-6"></div>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {isAdmin() ? (
                    <>
                        {topItems.map(renderNavLink)}

                        {/* Settings group with collapsible sub-menu */}
                        <div>
                            <button
                                onClick={() => setSettingsOpen(!settingsOpen)}
                                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-medium transition-all rounded-lg ${
                                    isOnSettingsPage && !settingsOpen
                                        ? 'bg-brand-lime/10 text-brand-charcoal'
                                        : 'text-brand-gray-600 hover:bg-brand-gray-50 hover:text-brand-charcoal'
                                }`}
                                data-testid="sidebar-nav-settings-toggle"
                            >
                                <div className="flex items-center gap-3">
                                    <Settings className={`h-5 w-5 ${isOnSettingsPage ? 'text-brand-lime' : ''}`} />
                                    Settings
                                </div>
                                <ChevronDown className={`h-4 w-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {settingsOpen && (
                                <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-brand-gray-100 pl-2">
                                    {settingsSubItems.map((item) => {
                                        const Icon = item.icon;
                                        const active = isActive(item.to);
                                        return (
                                            <Link
                                                key={item.to}
                                                to={item.to}
                                                className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all rounded-lg ${
                                                    active
                                                        ? 'bg-brand-lime/10 text-brand-charcoal'
                                                        : 'text-brand-gray-500 hover:bg-brand-gray-50 hover:text-brand-charcoal'
                                                }`}
                                                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                                            >
                                                <Icon className={`h-4 w-4 ${active ? 'text-brand-lime' : ''}`} />
                                                {item.label}
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    engineerItems.map(renderNavLink)
                )}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-border">
                <div className="flex items-center gap-3 px-2 py-2 mb-2">
                    <div className="h-10 w-10 bg-brand-lime flex items-center justify-center rounded-lg">
                        <User className="h-5 w-5 text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-charcoal truncate">{user?.name}</p>
                        <p className="text-xs text-brand-gray-500 truncate">{user?.email}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    data-testid="sidebar-logout-btn"
                >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                </Button>
            </div>
        </aside>
    );
};

// Mobile/Tablet Header
const MobileHeader = ({ appName }) => {
    const { user, logout, isAdmin, isGlobalAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const settingsSubPaths = ['/admin/settings', '/admin/users', '/admin/email-templates', '/admin/quote-settings', '/admin/app-settings', '/admin/logs', '/admin/report-designer'];
    const isOnSettingsPage = settingsSubPaths.some(p => location.pathname.startsWith(p));

    useEffect(() => {
        if (isOnSettingsPage) setSettingsOpen(true);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const topAdminItems = [
        { to: '/admin', label: 'Dashboard', icon: Home },
        { to: '/admin/quotes', label: 'Quotes', icon: FileText },
        { to: '/admin/spares', label: 'Spares', icon: Package },
        { to: '/admin/customers', label: 'Customers', icon: Users },
        { to: '/admin/engineer-history', label: 'Engineers', icon: BarChart3 },
    ];

    const settingsSubItems = [
        { to: '/admin/settings', label: 'General', icon: Settings },
        { to: '/admin/users', label: 'Users', icon: UserPlus },
        { to: '/admin/email-templates', label: 'Emails', icon: Mail },
        { to: '/admin/quote-settings', label: 'PDF Settings', icon: FileEdit },
        ...(isGlobalAdmin() ? [
            { to: '/admin/app-settings', label: 'App Settings', icon: Cog },
            { to: '/admin/logs', label: 'Logs', icon: ScrollText },
            { to: '/admin/report-designer', label: 'Report Designer', icon: Paintbrush },
        ] : []),
    ];

    const engineerItems = [
        { to: '/', label: 'New Quote', icon: PlusCircle },
        { to: '/quotes', label: 'My Quotes', icon: FileText },
    ];

    return (
        <header className="lg:hidden sticky top-0 z-40 w-full border-b border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
            <div className="container mx-auto px-4">
                <div className="flex h-16 items-center justify-between">
                    <Link to={isAdmin() ? '/admin' : '/'} className="text-lg font-bold font-heading text-brand-charcoal">
                        {appName || ''}
                    </Link>

                    {/* User Menu & Mobile Toggle */}
                    <div className="flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    className="flex items-center gap-2 h-10 px-3"
                                    data-testid="user-menu-trigger"
                                >
                                    <div className="h-8 w-8 bg-brand-lime flex items-center justify-center rounded-lg">
                                        <User className="h-4 w-4 text-black" />
                                    </div>
                                    <span className="hidden sm:inline text-sm font-medium">
                                        {user?.name?.split(' ')[0]}
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <div className="px-3 py-2">
                                    <p className="text-sm font-medium">{user?.name}</p>
                                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    <p className="text-xs text-brand-lime font-medium mt-1 uppercase">
                                        {user?.role === 'global_admin' ? 'Global Admin' : user?.role}
                                    </p>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    onClick={handleLogout}
                                    className="text-red-600 cursor-pointer"
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Mobile Menu Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <nav className="py-4 border-t border-border animate-fade-in">
                        {isAdmin() ? (
                            <>
                                {topAdminItems.map((item) => (
                                    <MobileNavLink key={item.to} to={item.to} icon={item.icon} onClick={() => setMobileMenuOpen(false)}>
                                        {item.label}
                                    </MobileNavLink>
                                ))}

                                {/* Settings group */}
                                <button
                                    onClick={() => setSettingsOpen(!settingsOpen)}
                                    className={`w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-lg ${
                                        isOnSettingsPage ? 'bg-brand-lime/10 text-brand-charcoal' : 'text-brand-gray-600 hover:bg-brand-gray-100'
                                    }`}
                                    data-testid="mobile-settings-toggle"
                                >
                                    <div className="flex items-center gap-3">
                                        <Settings className={`h-5 w-5 ${isOnSettingsPage ? 'text-brand-lime' : ''}`} />
                                        Settings
                                    </div>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {settingsOpen && (
                                    <div className="ml-6 border-l-2 border-brand-gray-100 pl-2">
                                        {settingsSubItems.map((item) => (
                                            <MobileNavLink key={item.to} to={item.to} icon={item.icon} onClick={() => setMobileMenuOpen(false)}>
                                                {item.label}
                                            </MobileNavLink>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            engineerItems.map((item) => (
                                <MobileNavLink key={item.to} to={item.to} icon={item.icon} onClick={() => setMobileMenuOpen(false)}>
                                    {item.label}
                                </MobileNavLink>
                            ))
                        )}
                    </nav>
                )}
            </div>
        </header>
    );
};

const MobileNavLink = ({ to, children, icon: Icon, onClick }) => {
    const location = useLocation();
    const isActive = location.pathname === to ||
                     (to !== '/' && to !== '/admin' && location.pathname.startsWith(to));

    return (
        <Link
            to={to}
            onClick={onClick}
            className={`flex items-center justify-between px-4 py-3 text-base font-medium rounded-lg ${
                isActive 
                    ? 'bg-brand-lime/10 text-brand-charcoal' 
                    : 'text-brand-gray-600 hover:bg-brand-gray-100'
            }`}
        >
            <div className="flex items-center gap-3">
                {Icon && <Icon className={`h-5 w-5 ${isActive ? 'text-brand-lime' : ''}`} />}
                {children}
            </div>
            <ChevronRight className="h-4 w-4 text-brand-gray-400" />
        </Link>
    );
};

const Layout = ({ children }) => {
    const { isAuthenticated, loading, token } = useAuth();
    const [appName, setAppName] = useState('');

    useEffect(() => {
        if (isAuthenticated && token) {
            fetchAppName();
        }
        
        // Listen for settings updates
        const handleSettingsUpdate = () => fetchAppName();
        window.addEventListener('appSettingsUpdated', handleSettingsUpdate);
        return () => window.removeEventListener('appSettingsUpdated', handleSettingsUpdate);
    }, [isAuthenticated, token]);

    const fetchAppName = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAppName(response.data.app_name || '');
        } catch (error) {
            console.error('Failed to fetch app name:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <>{children}</>;
    }

    return (
        <div className="min-h-screen bg-background flex">
            {/* Desktop Sidebar */}
            <Sidebar appName={appName} />
            
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Mobile/Tablet Header */}
                <MobileHeader appName={appName} />
                
                {/* Page Content */}
                <main className="flex-1 container mx-auto px-4 py-6 pb-24 lg:pb-6 overflow-x-hidden">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
